/**
 * A rider's cash: what they hold, where it came from, and handing it in.
 *
 * The ledger is shown alongside the deposit box on purpose. Somebody handing
 * in money is the person most likely to disagree about how much they owe, and
 * the answer to that is the history rather than an assurance.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR, formatServerTime } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

const MOVEMENT = {
  collection: 'Collected on delivery',
  deposit: 'Handed in',
  adjustment: 'Adjustment',
  penalty: 'Penalty',
};

const when = (value) => formatServerTime(value, { year: undefined });

export function RiderCashSheet({ rider, onClose }) {
  const client = useQueryClient();
  const [amount, setAmount] = useState('');

  const ledger = useQuery({
    queryKey: ['delivery', 'ledger', rider.id],
    queryFn: () => api.get(`/delivery/riders/${rider.id}/ledger`),
  });

  const deposit = useMutation({
    mutationFn: (body) => api.post(`/delivery/riders/${rider.id}/deposit`, body),
    onSuccess: () => {
      setAmount('');
      client.invalidateQueries({ queryKey: ['delivery'] });
    },
  });

  const held = ledger.data?.rider?.codBalance ?? rider.cod_balance ?? 0;

  /**
   * What this rider is trusted with.
   *
   * It could only ever be set on the day they were hired, and nothing could
   * change it afterwards — so a limit chosen for somebody's first shift stood
   * for ever, whichever way their reliability went.
   */
  const [limit, setLimit] = useState(String(rider.cod_limit ?? ''));

  const retrust = useMutation({
    mutationFn: () => api.put(`/delivery/riders/${rider.id}`, { codLimit: Number(limit) }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['delivery'] }),
  });
  const value = Number(amount) || 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${rider.name}'s cash`}
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-surface p-5">
        <div className="flex items-baseline justify-between">
          <h2 className="font-semibold text-lg text-ink">{rider.name}</h2>
          <span className="text-2xl font-semibold tabular-nums text-ink">{formatPKR(held)}</span>
        </div>
        <p className="mt-0.5 text-sm text-ink-2">of the shop&rsquo;s money</p>

        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-surface-2 p-3">
          <label htmlFor="limit" className="flex-1 text-sm text-ink-2">
            The most they may carry
          </label>
          <input
            id="limit"
            type="number"
            min="0"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            aria-label="The most they may carry"
            className="h-9 w-28 rounded-lg border border-rule bg-surface-3 px-2 text-right text-sm tabular-nums text-ink focus:border-brand-400 focus:outline-none"
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={String(limit) === String(rider.cod_limit) || retrust.isPending}
            onClick={() => retrust.mutate()}
          >
            {retrust.isPending ? 'Saving' : 'Save'}
          </Button>
        </div>

        {retrust.isError && (
          <p className="mt-2 text-sm text-bad">{retrust.error?.message ?? 'That did not work.'}</p>
        )}

        {held > 0 && (
          <div className="mt-4">
            <label htmlFor="deposit" className="mb-1.5 block text-sm font-medium text-ink-2">
              Handing in
            </label>
            <div className="flex gap-2">
              <input
                id="deposit"
                type="number"
                inputMode="decimal"
                min="0"
                max={held}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                className="h-12 min-w-0 flex-1 rounded-lg border border-rule bg-surface-3 px-3.5 text-lg font-semibold tabular-nums text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
              <Button variant="secondary" onClick={() => setAmount(String(held))}>
                All of it
              </Button>
            </div>

            {value > held && (
              <p role="alert" className="mt-1.5 text-sm text-bad">
                More than they are carrying. The most is {formatPKR(held)}.
              </p>
            )}

            {deposit.error && (
              <p role="alert" className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
                {deposit.error.message}
              </p>
            )}

            <Button
              size="lg"
              className="mt-3 w-full"
              busy={deposit.isPending}
              disabled={!(value > 0) || value > held}
              onClick={() => deposit.mutate({ amount: value })}
            >
              Record {value > 0 ? formatPKR(value) : 'handover'}
            </Button>
          </div>
        )}

        <h3 className="mt-5 mb-2 text-sm font-medium text-ink-2">Where it came from</h3>
        {ledger.isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner label="Loading" />
          </div>
        ) : (ledger.data?.entries ?? []).length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-3">Nothing yet.</p>
        ) : (
          <ul className="max-h-56 divide-y divide-rule overflow-y-auto rounded-lg border border-rule">
            {ledger.data.entries.map((entry) => (
              <li key={entry.id} className="flex items-baseline justify-between gap-2 p-2.5">
                <span className="min-w-0">
                  <span className="block text-sm text-ink">
                    {MOVEMENT[entry.transaction_type] ?? entry.transaction_type}
                  </span>
                  <span className="block text-xs text-ink-3">{when(entry.created_at)}</span>
                </span>
                <span className="shrink-0 text-right">
                  <span
                    className={`block text-sm font-medium tabular-nums ${
                      entry.amount < 0 ? 'text-good' : 'text-ink'
                    }`}
                  >
                    {entry.amount < 0 ? '−' : '+'}
                    {formatPKR(Math.abs(entry.amount))}
                  </span>
                  {/* The balance each movement produced, so the history can be
                      checked rather than taken on trust. */}
                  <span className="block text-xs tabular-nums text-ink-3">
                    {formatPKR(entry.balance_after)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}

        <Button variant="secondary" size="lg" className="mt-5 w-full" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
